import {
  getNumberFormatterFamily,
  getPercentScale,
  IFormatterDataModel,
  NumericFormatterId,
  StandardFormatterId,
} from '@foresightdata/web-common-core';
import { merge } from 'lodash-es';
import { FormatterService } from '@foresightdata/web-common-formatter-service';
import { GetterFormatType } from '../util/getFormatter';
import { createFormField, IDateFieldFormSettings, IFormField } from '../view-models/form.view-model';
import {
  createField3DataModel,
  getFormatterModelForField3,
  IField3,
  PrimitiveType,
  PrimitiveTypesAsArray,
} from './field3.data-model';
import { RendererType } from './field-definition';
import { KpiConfigPeriod } from './company-financials.data-model';

export type CustomData = Record<string, string | number | string[]>;

// In lockstep with the copy in @foresightdata/web-common-core: adding a member here makes our
// IField unassignable to the package's.
export enum GridRef {
  kpiDataCollection = 'kpiDataCollection',
  portfolioReporting = 'portfolioReporting',
  roundTracker = 'roundTracker',
  securityOverview = 'securityOverview',
}

// Outside GridRef for the reason above; fold in once web-common-core ships a matching member.
export const FundReportingGridRef = 'fundReporting';
export type FundReportingGridRef = typeof FundReportingGridRef;

export enum EntityFieldType {
  Custom = 'customData',
  Universal = 'universalFieldData',
}

export enum KpiConfigSection {
  Actual = 'actual',
  Budget = 'budget',
  Both = 'both',
}

export enum KpiConfigScenario {
  Accrued = 'accrued',
  EndOfPeriod = 'endOfPeriod',
  None = 'none',
}

export const KpiConfigScenarioToName: Record<KpiConfigScenario, string> = {
  [KpiConfigScenario.Accrued]: 'Accrued',
  [KpiConfigScenario.EndOfPeriod]: 'End of Period',
  [KpiConfigScenario.None]: 'None',
};

export type BasicFormConfig = Record<string, unknown>;

export interface IField<
  DataType,
  FormMetaType extends BasicFormConfig = BasicFormConfig,
  FormatterType extends BasicFormConfig = BasicFormConfig,
> {
  description: string | null;
  displayName: string;
  entity: string;
  entityField: string;
  sortOrder?: number;
  formMeta: IFieldFormConfig<DataType, FormMetaType, FormatterType> | null;
  gridMeta: IGridMetaConfig | null;
  systemMeta: {
    formRefs: string[];
    gridRefs: GridRef[];
    source: FieldSource;
  };
  id: number;
  type: string; // Primitive type, matches typescript type e.g. number, string, stringArray etc...
  createdBy?: string | null;
  createdAt?: string | null;
  kpiGroupId?: number;
  kpiScenario?: KpiConfigScenario;
  periods?: KpiConfigPeriod[];
  sectionType?: KpiConfigSection;
  timeSeries?: boolean | null;
  updatedBy?: string | null;
  yearCalcType?: string;
}

export enum FieldSource {
  Default = 'default',
  Foresight = 'foresight', // user entered in foresight
  KPICollection = 'kpiCollection',
}

export const EntityType = {
  company: 'company',
  companyInvestmentThesis: 'companyInvestmentThesis',
  fund: 'fund',
  fundCompany: 'fundCompany',
  person: 'person',
  round: 'round',
} as const;
export type EntityType = (typeof EntityType)[keyof typeof EntityType];

export const FieldEntity = {
  ...EntityType,
  captableInvestment: 'captableInvestment',
  deal: 'deal',
  dummy: 'dummy',
  people: 'people',
  scenario: 'scenario',
  security: 'security',
  systemKPI: 'systemKPI',
  universalKPI: 'universalKPI',
  userKPI: 'userKPI',
} as const;

export type FieldEntity = (typeof FieldEntity)[keyof typeof FieldEntity];

export enum FormOptions {
  addingCaptableSecurity = 'Adding Captable Security',
  addingTransaction = 'Adding Transaction',
  addingDeal = 'Adding Deal',
  companyLists = 'Company Lists',
  companyProfile = 'Company Profile',
  addingRound = 'Adding Round',
}

////////////////////////////
////
////  Form models
////
////////////////////////////

export interface IFieldFormConfig<
  DataType,
  FormMetaType extends BasicFormConfig,
  FormatterType extends BasicFormConfig = BasicFormConfig,
> {
  defaultValue: DataType | null;
  formatter: IFormatterDataModel<FormatterType>;
  renderer: IRendererConfig<FormMetaType> | null;
  required: boolean | null;
}

export interface IRendererConfig<T> {
  id?: string;
  type: RendererType | string;
  config: T;
}

export const KpiTemplateSectionType = {
  grid: 'grid',
  field: 'field',
  textualKpi: 'textualKpi',
} as const;
export type KpiTemplateSectionType = (typeof KpiTemplateSectionType)[keyof typeof KpiTemplateSectionType];

export interface IInputConfig extends Record<string, unknown> {
  placeholder: string | null;
}

export interface ISimpleChoice<T> {
  disabled?: boolean;
  displayName?: string | null;
  group?: string | null;
  secondaryLabel?: string | null;
  /** Second line of a two-line option. Ignored by the default single-line layout. */
  caption?: string | null;
  value: T;
}

// TextField
export interface ITextFieldConfig extends IInputConfig {
  maxLength: number | null;
}

export interface ICurrencyFieldConfig extends IInputConfig {
  currencyCode: string;
}

// Date
export interface IDateFieldConfig
  extends IInputConfig, Omit<IDateFieldFormSettings, 'disabled' | 'variant' | 'required'> {}

// Select
export interface ISelectConfig<T> extends IInputConfig {
  multi: boolean | null;
  values: ISimpleChoice<T>[];
}

////////////////////////////
////
////  Grid model
////
////////////////////////////

export interface IGridMetaConfig {
  aggFunc?: string | null;
  group?: string;
  hide?: boolean;
}

export function isNumericRenderer(type: RendererType) {
  return (
    type === RendererType.currency ||
    type === RendererType.percent ||
    type === RendererType.integer ||
    type === RendererType.multiplier ||
    type === RendererType.number ||
    type === RendererType.numeric
  );
}

/** Number renderers that encoded a format; now renderer `number` + that format as `formatter.id`. */
const DeprecatedNumberRenderers = new Set<string>([
  RendererType.currency,
  RendererType.integer,
  RendererType.multiplier,
  RendererType.numeric,
  RendererType.percent,
]);

export function isDeprecatedNumberRenderer(type: RendererType | string | undefined): boolean {
  return type != null && DeprecatedNumberRenderers.has(type);
}

export function isTextRenderer(type: RendererType) {
  return type === RendererType.text;
}

export function createFieldDataModel<
  DataType,
  FormRendererMetaType extends Record<string, unknown> = BasicFormConfig,
>(overrides: Partial<IField<DataType, FormRendererMetaType>> = {}): IField<DataType, FormRendererMetaType> {
  return merge(
    {
      description: null,
      displayName: '',
      entity: '',
      entityField: '',
      formMeta: createFieldFormConfigDataModel({}),
      gridMeta: null,
      id: 0,
      type: '',
      systemMeta: {
        formRefs: [],
        gridRefs: [],
        source: FieldSource.Default,
      },
      timeSeries: false,
    },
    overrides
  );
}

export function createFieldFormConfigDataModel<T, RendererConfig extends BasicFormConfig = BasicFormConfig>(
  overrides: Partial<IFieldFormConfig<T, RendererConfig>> = {}
): IFieldFormConfig<T, RendererConfig> {
  const { formatter, renderer, ...rest } = overrides;
  return {
    defaultValue: null,
    required: false,
    renderer: {
      id: 'string',
      type: RendererType.text,
      config: {} as RendererConfig,
      ...renderer,
    },
    formatter: {
      id: '',
      config: {},
      ...formatter,
    },
    ...rest,
  };
}

export function field2ToField3(field: IField<unknown>): IField3<unknown> {
  const { description, id, entity, entityField, formMeta, gridMeta, type, displayName, timeSeries } = field;
  const configV3 = createField3DataModel({
    aggFunc: gridMeta?.aggFunc ?? null,
    description: description ?? '',
    displayName,
    entity: entity as FieldEntity,
    entityKey: entityField,
    id,
    providerOrder: [],
    timeSeries,
    // Carried through so every field3 consumer (grid col defs, form fields, AgFormatterService)
    // resolves a chosen format the same way. Absent for a field on its derived default.
    formatter: getFieldFormatterOverride(field),
  });

  const rendererType = getKpiFormatRenderer(field) ?? formMeta?.renderer?.type;
  const rendererConfig = formMeta?.renderer?.config;
  const formatterConfig = formMeta?.formatter?.config;
  if (rendererType) {
    configV3.format = rendererType as RendererType;

    switch (rendererType) {
      case RendererType.boolean: {
        configV3.dataType = 'boolean';
        break;
      }
      case RendererType.text: {
        configV3.dataType = 'string';
        configV3.meta = {
          maxLength: rendererConfig?.maxLength ?? -1,
        };
        break;
      }
      case RendererType.integer:
      case RendererType.number: {
        configV3.dataType = 'number';
        break;
      }
      case RendererType.numeric: {
        configV3.dataType = 'number';
        configV3.format = RendererType.number;
        break;
      }
      case RendererType.currency: {
        configV3.dataType = 'number';
        configV3.meta = {
          defaultCurrency: formatterConfig?.currency ?? 'USD',
        };
        break;
      }
      case RendererType.percent: {
        configV3.dataType = 'number';
        break;
      }
      case RendererType.multiplier: {
        configV3.dataType = 'number';
        break;
      }
      case RendererType.date: {
        configV3.dataType = 'string';
        break;
      }
      case RendererType.singleSelect: {
        configV3.dataType = isPrimitiveDataType(type) ? type : 'string';
        configV3.meta = {
          values: rendererConfig?.values ?? [],
          multi: false,
        };
        break;
      }
      case RendererType.colorStatus:
      case RendererType.multiSelect: {
        configV3.dataType = 'array';
        configV3.meta = {
          values: rendererConfig?.values ?? [],
          multi: true,
        };
        break;
      }
    }

    return configV3;
  } else if (field.formMeta?.formatter) {
    const formatterType = field.formMeta.formatter.type as RendererType | string;
    configV3.format = formatterType as RendererType;

    switch (formatterType) {
      case 'string': {
        configV3.dataType = 'string';
        configV3.format = RendererType.text;
        configV3.meta = {
          maxLength: rendererConfig?.maxLength ?? -1,
        };
        break;
      }
      case RendererType.currency: {
        configV3.dataType = 'number';
        configV3.meta = {
          defaultCurrency: formatterConfig?.currency ?? 'USD',
        };
        break;
      }
      case RendererType.number:
      case RendererType.percent:
      case RendererType.multiplier: {
        configV3.dataType = 'number';
        break;
      }
      case RendererType.date: {
        configV3.dataType = 'string';
        break;
      }
      case RendererType.boolean: {
        configV3.dataType = 'boolean';
        break;
      }
    }
  } else {
    configV3.dataType = getPrimitiveFromFieldType(type);
    configV3.format = getRendererTypeFromFieldType(type);
  }
  return configV3;
}

/**
 * The primitive type a renderer's values are stored as. Shared by the KPI save path and the
 * formatter-override gate below, so "what kind of value is this" is answered in one place.
 */
export function rendererTypeToPrimitiveType(renderer: RendererType | string | undefined): PrimitiveType {
  switch (renderer) {
    case RendererType.boolean:
      return 'boolean';
    case RendererType.date:
      return 'date';
    case RendererType.currency:
    case RendererType.integer:
    case RendererType.multiplier:
    case RendererType.number:
    case RendererType.numeric:
    case RendererType.percent:
      return 'number';
    case RendererType.colorStatus:
    case RendererType.freeLabels:
    case RendererType.multiSelect:
      return 'array';
    case RendererType.text:
    default:
      return 'string';
  }
}

/** The formatter id the custom-field form derives from a renderer when nobody picks one. */
export function rendererToFormatterId(type: RendererType | string) {
  switch (type) {
    case RendererType.multiSelect:
      return 'stringArray';
    case RendererType.singleSelect:
    case RendererType.text:
      return 'string';
    default:
      return type;
  }
}

/**
 * Ids our admin forms stamp when nobody has chosen a format: the KPI form writes the renderer
 * type verbatim (`transformKpi`), the custom-field form writes `rendererToFormatterId`.
 */
function isDerivedFormatterId(renderer: RendererType | string, id: string): boolean {
  return id === renderer || id === rendererToFormatterId(renderer);
}

function getRegisteredFormatterModel(id: string): IFormatterDataModel<unknown> | undefined {
  try {
    return FormatterService.get().getFormatterModel(id as StandardFormatterId);
  } catch {
    // Formatter service not initialized (a unit test that never booted it): treat the id as
    // unrecognized so the field keeps its derived formatter.
    return undefined;
  }
}

/**
 * The formatter an admin deliberately chose for this field, or `undefined` while the field is
 * still on the default its renderer derives.
 *
 * Every field created before the Format picker existed carries an id stamped from its renderer,
 * so the read paths must divert only for a genuine choice. Propagating unconditionally would
 * flip every currency custom field from compact to full formatting across all clients.
 *
 * The type check guards records that carry a real formatter id from the wrong family — a string
 * formatter on a currency renderer, say — which would otherwise raw-stringify the value.
 */
export function getFieldFormatterOverride(field: IField<unknown>): IFormatterDataModel<unknown> | undefined {
  const formatter = field.formMeta?.formatter;
  const renderer = field.formMeta?.renderer?.type;
  if (!formatter?.id || !renderer || isDerivedFormatterId(renderer, formatter.id)) {
    return undefined;
  }

  // A composed currency formatter (`EUR-currencyShort`) is only registered lazily, so validate
  // the base it extends rather than the id itself.
  const model = getRegisteredFormatterModel(formatter.extends || formatter.id);
  if (!model) {
    return undefined;
  }
  // The registry is the authority on what an id formats; the stored formatter's own `type` is
  // not (createFormatterDataModel defaults it to 'string' for every custom field).
  const formatterPrimitive = model.type === 'currency' ? 'number' : model.type;

  return formatterPrimitive === rendererTypeToPrimitiveType(renderer) ? formatter : undefined;
}

/**
 * The deprecated renderer a Format stands for on the base `number` renderer, by the format's family
 * (`currencyShort` → currency), so code that branches on currency/percent/… renderers treats both
 * shapes alike. `undefined` for any other renderer.
 */
export function getKpiFormatRenderer(kpi: IField<unknown>): RendererType | undefined {
  if (kpi.formMeta?.renderer?.type !== RendererType.number) {
    return undefined;
  }
  const formatterId = kpi.formMeta.formatter?.id;
  const model = formatterId ? getRegisteredFormatterModel(formatterId) : undefined;
  if (!model) {
    return RendererType.number;
  }

  switch (getNumberFormatterFamily(model)) {
    case 'currency':
      return RendererType.currency;
    case 'percentage':
      // A fraction-scale format shows 0.45 as 45%; the percent renderer would add `%` to 0.45.
      return getPercentScale(model) === 'fraction' ? RendererType.number : RendererType.percent;
    case 'multiple':
      return RendererType.multiplier;
    case 'number':
      return formatterId === NumericFormatterId.integer ? RendererType.integer : RendererType.number;
    default:
      return RendererType.number;
  }
}

/**
 * Whether a field's format is a currency format (`currency`, `currencyShort`, `usd`, a client
 * format styled as currency): the api's rule for which KPIs it converts between currencies.
 */
export function hasCurrencyFormat(field: IField<unknown>): boolean {
  const formatterId = field.formMeta?.formatter?.id;
  const model = formatterId ? getRegisteredFormatterModel(formatterId) : undefined;
  return model != null && getNumberFormatterFamily(model) === 'currency';
}

export function getFormatterModelForField2(field: IField<unknown>): IFormatterDataModel<unknown> {
  const override = getFieldFormatterOverride(field);
  if (override) {
    return override;
  }
  if (field.formMeta?.formatter.config) {
    // Keep existing behaviour the same, only return this if there is some special customization
    return field.formMeta.formatter;
  }
  return getFormatterModelForField3(field2ToField3(field));
}

export function field2ToFormField<T>(
  field: IField<unknown>,
  overrides: Partial<IFormField<T>> = {}
): IFormField<T> {
  const { description, entityField, formMeta, type, displayName, timeSeries } = field;
  const rendererType = getKpiFormatRenderer(field) ?? formMeta?.renderer?.type;
  const formField = createFormField<unknown>({
    description: description ?? '',
    label: displayName,
    key: entityField,
    renderer: (rendererType as RendererType) ?? RendererType.text,
    // A chosen format, which is the branch getFormatterForFormField takes before falling through
    // to its renderer switch. The whole model rather than its id: a composed currency formatter
    // is registered lazily, so an id alone would miss the registry and stringify the value.
    // Spread before `overrides` so a caller's explicit formatter still wins.
    formatter: getFieldFormatterOverride(field),
    timeSeries,
    ...overrides,
  });

  const rendererConfig = formMeta?.renderer?.config;
  const formatterConfig = formMeta?.formatter?.config;
  if (rendererType) {
    formField.renderer = rendererType as RendererType;

    switch (rendererType) {
      case RendererType.boolean: {
        formField.dataType = 'boolean';
        break;
      }
      case RendererType.text: {
        formField.dataType = 'string';
        formField.rendererMeta = {
          maxLength: rendererConfig?.maxLength ?? -1,
        };
        break;
      }
      case RendererType.integer:
      case RendererType.number: {
        formField.dataType = 'number';
        break;
      }
      case RendererType.numeric: {
        formField.dataType = 'number';
        formField.renderer = RendererType.number;
        break;
      }
      case RendererType.currency: {
        formField.dataType = 'number';
        formField.rendererMeta = {
          defaultCurrency: formatterConfig?.currency ?? 'USD',
        };
        break;
      }
      case RendererType.percent: {
        formField.dataType = 'number';
        break;
      }
      case RendererType.multiplier: {
        formField.dataType = 'number';
        break;
      }
      case RendererType.date: {
        formField.dataType = 'string';
        break;
      }
      case RendererType.singleSelect: {
        formField.dataType = isPrimitiveDataType(type) ? type : 'string';
        formField.rendererMeta = {
          values: rendererConfig?.values ?? [],
          multi: false,
        };
        break;
      }
      case RendererType.multiSelect: {
        formField.dataType = 'array';
        formField.rendererMeta = {
          values: rendererConfig?.values ?? [],
          multi: true,
          allowCustomAdd: rendererConfig?.allowCustomAdd ?? false,
        };
        break;
      }
      case RendererType.colorStatus: {
        formField.dataType = 'array';
        formField.rendererMeta = {
          values: rendererConfig?.values ?? [],
          multi: true,
        };
        break;
      }
    }
  } else if (field.formMeta?.formatter) {
    const formatterType = field.formMeta.formatter.type as RendererType | string;
    formField.renderer = formatterType as RendererType;

    switch (formatterType) {
      case 'string': {
        formField.dataType = 'string';
        formField.renderer = RendererType.text;
        formField.rendererMeta = {
          maxLength: rendererConfig?.maxLength ?? -1,
        };
        break;
      }
      case RendererType.number: {
        formField.dataType = 'number';
        break;
      }
      case RendererType.currency: {
        formField.dataType = 'number';
        formField.rendererMeta = {
          defaultCurrency: formatterConfig?.currency ?? 'USD',
        };
        break;
      }
      case RendererType.percent: {
        formField.dataType = 'number';
        break;
      }
      case RendererType.multiplier: {
        formField.dataType = 'number';
        break;
      }
      case RendererType.date: {
        formField.dataType = 'string';
        break;
      }
      case RendererType.boolean: {
        formField.dataType = 'boolean';
        break;
      }
    }
  } else {
    formField.dataType = getPrimitiveFromFieldType(type);
    formField.renderer = getRendererTypeFromFieldType(type);
  }

  return formField as IFormField<T>;
}

function isPrimitiveDataType(type: string | undefined): type is PrimitiveType {
  if (!type) {
    return false;
  }
  return PrimitiveTypesAsArray.includes(type as PrimitiveType);
}

export function getRendererTypeFromFieldType(type: GetterFormatType | string | undefined): RendererType {
  switch (type) {
    case 'number':
      return RendererType.number;
    default:
      return RendererType.text;
  }
}
function getPrimitiveFromFieldType(type: string): PrimitiveType {
  if (isPrimitiveDataType(type)) {
    return type;
  }
  switch (type) {
    case 'stringArray':
    case 'numberArray':
      return 'array';
    default:
      return 'string';
  }
}

export function isUniversalKPIField(field: IField<unknown>) {
  return field.entity === FieldEntity.universalKPI || field.entity === FieldEntity.systemKPI;
}
